/**
 * @file apps/web/src/pages/portal/PortalScriptsPage.tsx
 * @description Client portal test scripts — read-only view with status filtering.
 * Clients can view scripts and their steps, but cannot edit them.
 * Defects associated with the project are shown in a secondary section.
 * Route: /portal/scripts
 */

import { useEffect, useState } from 'react';
import {
  ClipboardList, Bug, CheckCircle, XCircle, Clock,
  ChevronDown, ChevronRight, AlertCircle, Search,
} from 'lucide-react';
import { usePortalAuth } from '../../lib/portalAuth.js';
import { testScriptsApi, uatApi } from '../../lib/api.js';
import { Badge } from '../../components/Badge.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ScriptStep {
  step_number:     number;
  description:     string;
  expected_result?: string | null;
}

interface TestScript {
  id:          string;
  title:       string;
  module:      string;
  status:      string;
  description?: string | null;
  steps?:      ScriptStep[];
}

interface Defect {
  id:          string;
  title:       string;
  status:      string;
  severity:    string;
  description?: string | null;
  created_at:  string;
}

// ---------------------------------------------------------------------------
// Script card (read-only, expandable)
// ---------------------------------------------------------------------------

function ScriptCard({ script }: { script: TestScript }) {
  const [expanded, setExpanded] = useState(false);
  const steps = script.steps ?? [];

  const statusIcon = {
    passed:      <CheckCircle size={13} className="text-success" />,
    failed:      <XCircle size={13} className="text-danger" />,
    in_progress: <Clock size={13} className="text-blue" />,
    draft:       <Clock size={13} className="text-muted" />,
  }[script.status] ?? <Clock size={13} className="text-muted" />;

  return (
    <div className="card overflow-hidden">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left p-4 flex items-center justify-between gap-3 hover:bg-sky/20 transition-colors"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {statusIcon}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-ink truncate">{script.title}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[11px] font-mono text-muted">{script.module}</span>
              <span className="text-[10px] font-mono text-muted/50">·</span>
              <span className="text-[11px] font-mono text-muted">{steps.length} steps</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge variant={script.status as never} size="xs" />
          {expanded
            ? <ChevronDown size={14} className="text-muted" />
            : <ChevronRight size={14} className="text-muted" />
          }
        </div>
      </button>

      {expanded && (
        <div className="border-t border-[rgba(8,17,31,0.06)] p-4 space-y-3">
          {script.description && (
            <p className="text-sm text-muted">{script.description}</p>
          )}
          {steps.length > 0 ? (
            <div className="space-y-2">
              {steps.map((step, i) => (
                <div key={i} className="flex gap-3 text-sm">
                  <span className="font-mono text-[11px] text-muted w-5 shrink-0 pt-0.5">
                    {step.step_number}.
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-ink">{step.description}</p>
                    {step.expected_result && (
                      <p className="text-[11px] font-mono text-muted mt-0.5">
                        Expected: {step.expected_result}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted/60 italic">No steps defined.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Defect row
// ---------------------------------------------------------------------------

function DefectRow({ defect }: { defect: Defect }) {
  const severityColor = {
    critical: 'text-danger bg-danger/10',
    high:     'text-danger bg-danger/6',
    medium:   'text-warning bg-warning/10',
    low:      'text-muted bg-gray',
  }[defect.severity] ?? 'text-muted bg-gray';

  return (
    <div className="card p-3 flex items-start gap-3">
      <Bug size={14} className="text-muted shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-ink truncate">{defect.title}</p>
        {defect.description && (
          <p className="text-[11px] text-muted mt-0.5 line-clamp-2">{defect.description}</p>
        )}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <span className={['text-[10px] font-mono px-1.5 py-0.5 rounded-full', severityColor].join(' ')}>
          {defect.severity}
        </span>
        <Badge variant={defect.status as never} size="xs" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PortalScriptsPage
// ---------------------------------------------------------------------------

type FilterStatus = 'all' | 'passed' | 'failed' | 'in_progress' | 'draft';

export function PortalScriptsPage() {
  const { user, devHeaders } = usePortalAuth();

  const [scripts, setScripts]   = useState<TestScript[]>([]);
  const [defects, setDefects]   = useState<Defect[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [filter, setFilter]     = useState<FilterStatus>('all');
  const [search, setSearch]     = useState('');
  const [activeTab, setActiveTab] = useState<'scripts' | 'defects'>('scripts');

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      testScriptsApi.list(user.projectId, devHeaders) as Promise<TestScript[]>,
      uatApi.listDefects(user.projectId, devHeaders) as Promise<Defect[]>,
    ])
      .then(([s, d]) => {
        if (!cancelled) { setScripts(s); setDefects(d); }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load scripts');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user?.projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredScripts = scripts.filter((s) => {
    if (filter !== 'all' && s.status !== filter) return false;
    if (search && !s.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const openDefects = defects.filter((d) => d.status !== 'resolved' && d.status !== 'closed');

  const STATUS_FILTERS: { key: FilterStatus; label: string; count: number }[] = [
    { key: 'all',         label: 'All',         count: scripts.length },
    { key: 'passed',      label: 'Passed',      count: scripts.filter((s) => s.status === 'passed').length },
    { key: 'failed',      label: 'Failed',      count: scripts.filter((s) => s.status === 'failed').length },
    { key: 'in_progress', label: 'In Progress', count: scripts.filter((s) => s.status === 'in_progress').length },
    { key: 'draft',       label: 'Draft',       count: scripts.filter((s) => s.status === 'draft').length },
  ];

  return (
    <div className="p-8 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="page-title">Test Scripts</h1>
        <p className="text-sm text-muted mt-1">
          Review the test scripts for your ERP implementation.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm mb-4">
          <AlertCircle size={14} className="shrink-0" /> {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[rgba(8,17,31,0.08)] mb-4">
        {[
          { key: 'scripts', label: 'Test Scripts', icon: <ClipboardList size={14} />, count: scripts.length },
          { key: 'defects', label: 'Defects', icon: <Bug size={14} />, count: openDefects.length },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key as 'scripts' | 'defects')}
            className={[
              'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === t.key
                ? 'border-blue text-blue'
                : 'border-transparent text-muted hover:text-ink',
            ].join(' ')}
          >
            {t.icon}
            {t.label}
            {t.count > 0 && (
              <span className={[
                'text-[10px] font-mono px-1.5 py-0.5 rounded-full',
                activeTab === t.key ? 'bg-blue/10 text-blue' : 'bg-gray text-muted',
              ].join(' ')}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'scripts' && (
        <div className="space-y-3">
          {/* Toolbar */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-xs">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search scripts…"
                className="input pl-8 text-sm"
              />
            </div>
            <div className="flex items-center gap-1">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={[
                    'text-[11px] font-mono px-2 py-1 rounded transition-colors',
                    filter === f.key
                      ? 'bg-blue text-white'
                      : 'text-muted hover:text-ink hover:bg-gray',
                  ].join(' ')}
                >
                  {f.label}
                  {f.count > 0 && ` (${f.count})`}
                </button>
              ))}
            </div>
          </div>

          {/* Script list */}
          {loading ? (
            <div className="space-y-2 animate-pulse">
              {[1,2,3,4].map((i) => <div key={i} className="card h-14 bg-gray/60" />)}
            </div>
          ) : filteredScripts.length === 0 ? (
            <div className="empty-state py-10">
              <ClipboardList size={24} className="text-muted/40 mb-2 mx-auto" />
              <p className="text-sm text-muted">No scripts match your filter.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredScripts.map((s) => (
                <ScriptCard key={s.id} script={s} />
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'defects' && (
        <div className="space-y-3">
          {loading ? (
            <div className="space-y-2 animate-pulse">
              {[1,2,3].map((i) => <div key={i} className="card h-14 bg-gray/60" />)}
            </div>
          ) : defects.length === 0 ? (
            <div className="empty-state py-10">
              <Bug size={24} className="text-muted/40 mb-2 mx-auto" />
              <p className="text-sm text-muted">No defects found.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {openDefects.length > 0 && (
                <div>
                  <p className="label mb-2">Open ({openDefects.length})</p>
                  {openDefects.map((d) => <DefectRow key={d.id} defect={d} />)}
                </div>
              )}
              {defects.filter((d) => d.status === 'resolved' || d.status === 'closed').length > 0 && (
                <div className="mt-3">
                  <p className="label mb-2">Resolved</p>
                  {defects
                    .filter((d) => d.status === 'resolved' || d.status === 'closed')
                    .map((d) => <DefectRow key={d.id} defect={d} />)
                  }
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
